import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import { Add, Delete, Deselect, FileDownload, PlaylistAdd, Print } from "@mui/icons-material";
import {
  ListDivider,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Menu,
  MenuItem,
} from "@mui/joy";
import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { useCurrentVenueLineLookup, useExportRoutesMutation } from "../../hooks/routes";
import toRouteLine from "../../utils/toRouteLine";
import { useModal } from "../modals/useModal";
import { useRoutesUiStore } from "../RoutesProvider";
import { useRoutesUiContext } from "../RoutesProvider/RoutesProvider";
import {
  ContextMenuProps,
  LineContextMenuProps,
  MapContextMenuProps,
  RouteContextMenuProps,
} from "../RoutesProvider/types";
import { useRoutesTableStore } from "./RoutesProvider2";

export const useVirtualElement = (contextMenu: ContextMenuProps | null) => {
  return useMemo(() => {
    if (!contextMenu) {
      return undefined;
    }
    const getBoundingClientRect = () => ({
      top: contextMenu.y,
      left: contextMenu.x,
      right: contextMenu.x,
      bottom: contextMenu.y,
      width: 0,
      height: 0,
      x: contextMenu.x,
      y: contextMenu.y,
      toJSON: () => "",
    });
    return {
      getBoundingClientRect,
    };
  }, [contextMenu]);
};

const RouteContextMenu = ({ contextMenu }: { contextMenu: RouteContextMenuProps }) => {
  const { canChangeSelection } = useRoutesUiContext();
  const { selectedRouteUuids, addToSelection, removeFromSelection } = useRoutesTableStore(
    useShallow((state) => ({
      selectedRouteUuids: state.selectedRouteUuids,
      addToSelection: state.addToSelection,
      removeFromSelection: state.removeFromSelection,
    })),
  );
  const setContextMenu = useRoutesUiStore((state) => state.setContextMenu);

  const virtualElement = useVirtualElement(contextMenu);
  const inSelectedSet = contextMenu.routeUuid && selectedRouteUuids.has(contextMenu.routeUuid);

  return (
    <ClickAwayListener onClickAway={() => setContextMenu(null)}>
      <Menu size="sm" anchorEl={virtualElement} open placement="bottom-start">
        {inSelectedSet && selectedRouteUuids.size > 1 ? (
          <ListItem>
            <ListItemContent sx={{ color: "var(--joy-palette-neutral-500)" }}>
              {selectedRouteUuids.size} routes selected
            </ListItemContent>
          </ListItem>
        ) : null}
        <MenuItem
          onClick={() => {
            if (!contextMenu) {
              return;
            }
            setContextMenu(null);
            inSelectedSet
              ? removeFromSelection([contextMenu.routeUuid])
              : addToSelection([contextMenu.routeUuid]);
          }}
          disabled={!canChangeSelection}
        >
          <ListItemDecorator>{inSelectedSet ? <Deselect /> : <PlaylistAdd />}</ListItemDecorator>
          <ListItemContent>
            {inSelectedSet ? "Remove from selection" : "Add to selection"}
          </ListItemContent>
        </MenuItem>
        <TableContextMenuContent
          onActionClick={() => setContextMenu(null)}
          routeUuid={contextMenu?.routeUuid}
        />
      </Menu>
    </ClickAwayListener>
  );
};

export const LineContextMenu = ({ contextMenu }: { contextMenu: LineContextMenuProps }) => {
  const { canChangeSelection, routes, onAddRoute } = useRoutesUiContext();
  const { addToSelection, removeFromSelection, selectedRouteUuids } = useRoutesTableStore(
    useShallow((state) => ({
      addToSelection: state.addToSelection,
      removeFromSelection: state.removeFromSelection,
      selectedRouteUuids: state.selectedRouteUuids,
    })),
  );
  const setContextMenu = useRoutesUiStore((state) => state.setContextMenu);

  const virtualElement = useVirtualElement(contextMenu);
  const line = useCurrentVenueLineLookup(
    contextMenu && "lineUuid" in contextMenu ? contextMenu.lineUuid : undefined,
  );
  const lineRoutes = useMemo(() => {
    return routes?.filter((route) => route.startLine?.uuid === contextMenu.lineUuid);
  }, [routes, contextMenu.lineUuid]);
  const routesCount = lineRoutes?.length || 0;

  const allRoutesSelected =
    selectedRouteUuids.size > 0 && lineRoutes?.every((route) => selectedRouteUuids.has(route.uuid));

  if (contextMenu?.type === "line") {
    return (
      <ClickAwayListener onClickAway={() => setContextMenu(null)}>
        <Menu size="sm" anchorEl={virtualElement} open placement="bottom-start">
          <ListItem>
            <ListItemContent
              sx={{
                color: "var(--joy-palette-neutral-600)",
                fontWeight: "bold",
              }}
            >
              Line: {line?.name} ({routesCount} route
              {routesCount !== 1 ? "s" : ""})
            </ListItemContent>
          </ListItem>
          {!allRoutesSelected ? (
            <MenuItem
              onClick={() => {
                setContextMenu(null);
                addToSelection(lineRoutes?.map((route) => route.uuid) || []);
              }}
              disabled={!canChangeSelection}
            >
              <ListItemDecorator>
                <PlaylistAdd />
              </ListItemDecorator>
              <ListItemContent>Add to Selection</ListItemContent>
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => {
                setContextMenu(null);
                removeFromSelection(lineRoutes?.map((route) => route.uuid) || []);
              }}
            >
              <ListItemDecorator>
                <Deselect />
              </ListItemDecorator>
              <ListItemContent>Remove from Selection</ListItemContent>
            </MenuItem>
          )}
          <ListDivider />
          <MenuItem
            onClick={() => {
              onAddRoute({
                startLine: line ? toRouteLine(line) : undefined,
              });
              setContextMenu(null);
            }}
            disabled={!canChangeSelection}
          >
            <ListItemDecorator>
              <Add />
            </ListItemDecorator>
            <ListItemContent>Create a new route</ListItemContent>
          </MenuItem>
        </Menu>
      </ClickAwayListener>
    );
  }

  return null;
};

export const MapContextMenu = ({ contextMenu }: { contextMenu: MapContextMenuProps }) => {
  const { onAddRoute, canChangeSelection } = useRoutesUiContext();
  const setContextMenu = useRoutesUiStore((state) => state.setContextMenu);

  const virtualElement = useVirtualElement(contextMenu);

  if (contextMenu?.type === "map") {
    return (
      <ClickAwayListener onClickAway={() => setContextMenu(null)}>
        <Menu size="sm" anchorEl={virtualElement} open placement="bottom-start">
          <MenuItem
            onClick={() => {
              onAddRoute({
                mapPosition: contextMenu.mapPosition,
                map: contextMenu.mapUuid,
              });
              setContextMenu(null);
            }}
            disabled={!canChangeSelection}
          >
            <ListItemDecorator>
              <Add />
            </ListItemDecorator>
            <ListItemContent>Create a route here</ListItemContent>
          </MenuItem>
        </Menu>
      </ClickAwayListener>
    );
  }

  return null;
};

// export const ContextMenu = () => {
//   const {
//     selectedRouteUuids,
//     addToSelection,
//     removeFromSelection,
//     contextMenu,
//     setContextMenu,
//     onAddRoute,
//   } = useRoutesUi();

//   const displayContextMenu = contextMenu !== null;

//   const virtualElement = useMemo(() => {
//     if (!displayContextMenu) {
//       return undefined;
//     }
//     const getBoundingClientRect = () => ({
//       top: contextMenu.y,
//       left: contextMenu.x,
//       right: contextMenu.x,
//       bottom: contextMenu.y,
//       width: 0,
//       height: 0,
//       x: contextMenu.x,
//       y: contextMenu.y,
//       toJSON: () => "",
//     });
//     return {
//       getBoundingClientRect,
//     };
//   }, [displayContextMenu, contextMenu?.x, contextMenu?.y]);

//   const line = useCurrentVenueLineLookup(
//     contextMenu && "lineUuid" in contextMenu ? contextMenu?.lineUuid : undefined
//   );

//   if (contextMenu?.type === "map") {
//     return (
//       <ClickAwayListener onClickAway={() => setContextMenu(null)}>
//         <Menu
//           size="sm"
//           anchorEl={virtualElement}
//           open={contextMenu !== null}
//           placement="bottom-start"
//         >
//           <MenuItem
//             onClick={() => {
//               onAddRoute({
//                 mapPosition: contextMenu.mapPosition,
//                 map: contextMenu.mapUuid,
//               });
//               setContextMenu(null);
//             }}
//           >
//             <ListItemDecorator>
//               <Add />
//             </ListItemDecorator>
//             <ListItemContent>Create a route here</ListItemContent>
//           </MenuItem>
//         </Menu>
//       </ClickAwayListener>
//     );
//   }

//   if (contextMenu?.type === "line") {
//     return (
//       <ClickAwayListener onClickAway={() => setContextMenu(null)}>
//         <Menu
//           size="sm"
//           anchorEl={virtualElement}
//           open={contextMenu !== null}
//           placement="bottom-start"
//         >
//           <ListItem>
//             <ListItemContent sx={{ color: "var(--joy-palette-neutral-500)" }}>
//               Line: {line?.name}
//             </ListItemContent>
//           </ListItem>
//           <MenuItem
//             onClick={() => {
//               setContextMenu(null);
//               addToSelection([contextMenu.lineUuid]);
//             }}
//           >
//             <ListItemDecorator>
//               <Add />
//             </ListItemDecorator>
//             <ListItemContent>Add to selection</ListItemContent>
//           </MenuItem>
//           <MenuItem
//             onClick={() => {
//               setContextMenu(null);
//               removeFromSelection([contextMenu.lineUuid]);
//             }}
//           >
//             <ListItemDecorator>
//               <Deselect />
//             </ListItemDecorator>
//             <ListItemContent>Remove from selection</ListItemContent>
//           </MenuItem>
//           <MenuItem
//             onClick={() => {
//               onAddRoute({
//                 startLine: contextMenu.lineUuid,
//               });
//               setContextMenu(null);
//             }}
//           >
//             <ListItemDecorator>
//               <Add />
//             </ListItemDecorator>
//             <ListItemContent>Create a route here</ListItemContent>
//           </MenuItem>
//         </Menu>
//       </ClickAwayListener>
//     );
//   }

//   const inSelectedSet =
//     contextMenu?.routeUuid && selectedRouteUuids.has(contextMenu.routeUuid);

//   return (
//     <ClickAwayListener onClickAway={() => setContextMenu(null)}>
//       <Menu
//         size="sm"
//         anchorEl={virtualElement}
//         open={contextMenu !== null}
//         placement="bottom-start"
//       >
//         {inSelectedSet && selectedRouteUuids.size > 1 ? (
//           <ListItem>
//             <ListItemContent sx={{ color: "var(--joy-palette-neutral-500)" }}>
//               {selectedRouteUuids.size} routes selected
//             </ListItemContent>
//           </ListItem>
//         ) : null}
//         <MenuItem
//           onClick={() => {
//             if (!contextMenu) {
//               return;
//             }
//             setContextMenu(null);
//             inSelectedSet
//               ? removeFromSelection([contextMenu.routeUuid])
//               : addToSelection([contextMenu.routeUuid]);
//           }}
//         >
//           <ListItemDecorator>
//             {inSelectedSet ? <Deselect /> : <Add />}
//           </ListItemDecorator>
//           <ListItemContent>
//             {inSelectedSet ? "Remove from selection" : "Add to selection"}
//           </ListItemContent>
//         </MenuItem>
//         <TableContextMenuContent
//           onActionClick={() => setContextMenu(null)}
//           routeUuid={contextMenu?.routeUuid}
//         />
//       </Menu>
//     </ClickAwayListener>
//   );
// };

export const TableContextMenuContent = ({
  routeUuid,
  onActionClick,
}: {
  routeUuid?: string;
  onActionClick?: () => void;
}) => {
  const selectedRouteUuids = useRoutesTableStore((state) => state.selectedRouteUuids);
  // const { canChangeSelection } = useRoutesUiContext();
  const exportDataMutation = useExportRoutesMutation();
  const { display: displayModal } = useModal();

  const routeUuids = useMemo(() => {
    if (routeUuid && !selectedRouteUuids.has(routeUuid)) {
      return [routeUuid];
    }
    return [...selectedRouteUuids];
  }, [routeUuid, selectedRouteUuids]);

  const onExportData = useCallback(() => {
    exportDataMutation.mutateAsync(routeUuids).then((result) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(result.blob);
      a.download = result?.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    });
    // TODO: Catch error and display a snackbar
    onActionClick?.();
  }, [exportDataMutation, onActionClick, routeUuids]);

  const setSelectedRouteUuids = useRoutesTableStore((state) => state.setSelectedRouteUuids);

  return (
    <>
      <MenuItem
        onClick={() => {
          displayModal("STRIP_ROUTES", {
            routeUuids,
            onComplete: () => {
              setSelectedRouteUuids([]);
            },
          });
          onActionClick?.();
        }}
      >
        <ListItemDecorator>
          <Delete />
        </ListItemDecorator>
        <ListItemContent>Strip...</ListItemContent>
      </MenuItem>
      <MenuItem
        onClick={() => {
          displayModal("PRINT_ROUTES", { routeUuids });
          onActionClick?.();
        }}
      >
        <ListItemDecorator>
          <Print />
        </ListItemDecorator>
        <ListItemContent>Print...</ListItemContent>
      </MenuItem>
      <MenuItem onClick={onExportData}>
        <ListItemDecorator>
          <FileDownload />
        </ListItemDecorator>
        <ListItemContent>Export Data</ListItemContent>
      </MenuItem>
    </>
  );
};

export default function ContextMenu() {
  const contextMenu = useRoutesUiStore((s) => s.contextMenu);

  if (!contextMenu) {
    return null;
  }

  switch (contextMenu.type) {
    case "route":
      return <RouteContextMenu contextMenu={contextMenu} />;
    case "line":
      return <LineContextMenu contextMenu={contextMenu} />;
    case "map":
      return <MapContextMenu contextMenu={contextMenu} />;
    default:
      return null;
  }
}
